import React, { useState, useEffect } from "react";
import { Link, useParams } from "react-router-dom";
import useSWR from "swr";
import { toast } from "sonner";
import { Pencil, X, ChevronRight, ExternalLink } from "lucide-react";
import Header from "@/components/Header";
import { Card, TextInput, Button } from "@/components/ui-kit";
import Markdown from "@/components/Markdown";
import { getPlan, updatePlan } from "@/lib/api";
import { getUser } from "@/lib/auth";
import { PLAN_RESOURCES } from "@/constants/records";

export default function PlanDetail() {
  const { number } = useParams();
  const n = Number(number);
  const isAdmin = getUser()?.rol === "admin";
  const { data: plan, mutate } = useSWR(["plan", n], () => getPlan(n));

  const [editing, setEditing] = useState(false);
  const [draftTitle, setDraftTitle] = useState("");
  const [draftMd, setDraftMd] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (plan) { setDraftTitle(plan.title || ""); setDraftMd(plan.content_md || ""); }
  }, [plan]);

  const save = async () => {
    if (!draftTitle.trim()) return toast.error("El pla ha de tenir títol");
    setSaving(true);
    try {
      await updatePlan(n, { title: draftTitle.trim(), content_md: draftMd, attachments: plan?.attachments || [] });
      toast.success("Pla desat");
      setEditing(false);
      mutate();
    } catch (err) { toast.error(err.response?.data?.detail || "Error desant el pla"); }
    finally { setSaving(false); }
  };

  const resources = (PLAN_RESOURCES[n] || []).filter((r) => !r.admin || isAdmin);

  return (
    <>
      <Header title={`Pla ${n}`} />
      <div className="mx-auto max-w-2xl px-4 pb-24 pt-6 sm:px-6 sm:pt-10">
        {!plan && <Card className="text-center text-sm text-ink-soft">Carregant…</Card>}
        {plan && (
          <>
            <div className="animate-fade-up flex items-start justify-between gap-3">
              <div>
                <div className="label-eyebrow">Pla {plan.number} · Sistema d'autocontrol</div>
                <h2 className="mt-1 font-display text-2xl font-bold leading-tight text-ink sm:text-3xl">{plan.title}</h2>
              </div>
              {isAdmin && !editing && (
                <button onClick={() => setEditing(true)} title="Editar el pla"
                  className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full text-ink-soft hover:bg-muted hover:text-ink" aria-label="Editar">
                  <Pencil size={18} />
                </button>
              )}
            </div>

            {resources.length > 0 && !editing && (
              <div className="mt-5 grid grid-cols-1 gap-2 sm:grid-cols-2">
                {resources.map((r) => (
                  <Link key={r.to + r.label} to={r.to}
                    className="group flex items-center justify-between gap-2 rounded-xl border border-line bg-white px-4 py-3 text-sm font-semibold text-ink shadow-card transition-all hover:-translate-y-0.5 hover:shadow-soft">
                    <span className="inline-flex items-center gap-2"><ExternalLink size={14} className="text-terracotta" /> {r.label}</span>
                    <ChevronRight size={15} className="text-ink-soft transition-transform group-hover:translate-x-1" />
                  </Link>
                ))}
              </div>
            )}

            {editing ? (
              <Card className="mt-5">
                <div className="space-y-4">
                  <div>
                    <div className="label-eyebrow mb-2">Títol</div>
                    <TextInput value={draftTitle} onChange={(e) => setDraftTitle(e.target.value)} />
                  </div>
                  <div>
                    <div className="label-eyebrow mb-2">Contingut (markdown: ### títols · | taules · &gt; avisos)</div>
                    <textarea value={draftMd} onChange={(e) => setDraftMd(e.target.value)} rows={22}
                      className="w-full rounded-xl border border-line bg-white px-4 py-3 font-mono text-xs leading-relaxed text-ink outline-none focus:border-terracotta focus:ring-2 focus:ring-terracotta/20" />
                  </div>
                  <div className="flex gap-2">
                    <Button onClick={save} disabled={saving} className="flex-1">{saving ? "Desant…" : "Desar el pla"}</Button>
                    <Button variant="ghost" onClick={() => { setEditing(false); setDraftTitle(plan.title); setDraftMd(plan.content_md || ""); }}>
                      <X size={16} /> Cancel·lar
                    </Button>
                  </div>
                </div>
              </Card>
            ) : (
              <Card className="mt-5">
                {plan.content_md
                  ? <Markdown text={plan.content_md} />
                  : <div className="text-sm text-ink-soft">Aquest pla encara no té contingut.</div>}
              </Card>
            )}

            {plan.updated_at && !editing && (
              <div className="mt-4 text-center text-xs text-ink-soft">
                Última actualització: {new Date(plan.updated_at).toLocaleDateString("ca-ES", { day: "2-digit", month: "long", year: "numeric" })}
              </div>
            )}
          </>
        )}
      </div>
    </>
  );
}
